import { createRouter, createWebHistory } from "vue-router";
import EcosystemView from "../views/EcosystemView.vue";
import { superAdminRouteGuard } from "../services/superAdminAccess";
import {
  authenticationRouteGuard,
  dashboardSectionGuard,
} from "./routePolicy";
import developmentOnlyRoutes from "@orus-development-only-routes";

const routes = [
  {
    path: "/",
    name: "home",
    component: EcosystemView,
    meta: { activeTab: "ecosystem" },
  },
  {
    path: "/ecosystem",
    name: "ecosystem",
    redirect: (to) => ({ path: "/", query: to.query, hash: to.hash }),
  },
  {
    path: "/invite/:orgUID/:inviteUID?",
    name: "accept-invite",
    component: () =>
      import(/* webpackChunkName: "invite" */ "../views/AcceptInviteView.vue"),
    meta: { requiresInvite: true },
  },
  {
    path: "/invites",
    name: "invites",
    component: () =>
      import(/* webpackChunkName: "invite" */ "../views/InvitesView.vue"),
    meta: { requiresAuth: true, activeTab: "invites" },
  },
  {
    path: "/about",
    name: "about",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
  },
  {
    path: "/legal/terms",
    name: "legal-terms",
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/LegalView.vue"),
    props: { document: "terms" },
  },
  {
    path: "/legal/data-policy",
    name: "legal-data-policy",
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/LegalView.vue"),
    props: { document: "data-policy" },
  },
  {
    path: "/account",
    name: "account",
    component: () =>
      import(/* webpackChunkName: "account" */ "../views/AccountView.vue"),
    meta: { requiresAuth: true, activeTab: "account" },
  },
  {
    path: "/account/appearance",
    name: "organisation-appearance",
    component: () =>
      import(
        /* webpackChunkName: "organisation-appearance" */ "../views/OrganisationAppearanceView.vue"
      ),
    meta: { requiresAuth: true, activeTab: "account" },
  },
  {
    path: "/organisations/new",
    name: "organisation-new",
    component: () =>
      import(
        /* webpackChunkName: "organisation-create" */ "../views/CreateOrganisationView.vue"
      ),
    meta: { requiresAuth: true, activeTab: "dashboard" },
  },
  {
    path: "/wallet/:section",
    redirect: (to) => ({
      name: "wallet",
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/wallet",
    name: "wallet",
    component: () =>
      import(/* webpackChunkName: "wallet" */ "../views/WalletView.vue"),
    meta: { requiresAuth: true, activeTab: "wallet" },
  },
  {
    path: "/login",
    name: "Login",
    meta: { directAuthLayout: true, guestOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "login" */ "../components/AuthComponents/Login.vue"
      ),
  },
  {
    path: "/register",
    name: "Register",
    meta: { directAuthLayout: true, guestOnly: true },
    component: () =>
      import(
        /* webpackChunkName: "register" */ "../components/AuthComponents/Register.vue"
      ),
  },
  {
    path: "/dashboard/:section?",
    name: "dashboard",
    component: () =>
      import(/* webpackChunkName: "dashboard" */ "../views/DashboardView.vue"),
    beforeEnter: dashboardSectionGuard,
    meta: { requiresAuth: true, activeTab: "dashboard" },
  },
  {
    path: "/org/:orgUID",
    name: "OrganisationView",
    component: () =>
      import(
        /* webpackChunkName: "userprofile" */ "../views/OrganisationView.vue"
      ),
  },
  {
    path: "/userprofile/:userUID",
    name: "UserProfile",
    component: () =>
      import(
        /* webpackChunkName: "userprofile" */ "../views/UserProfileView.vue"
      ),
  },
  {
    path: "/role/:roleUID/:orgUID",
    name: "RoleView",
    component: () =>
      import(/* webpackChunkName: "roleview" */ "../views/RoleView.vue"),
    meta: { usePathKey: true },
  },
  {
    path: "/product/:id",
    name: "ProductView",
    redirect: (to) => ({ path: "/", query: { item: to.params.id } }),
  },
  {
    path: "/super-admin",
    name: "super-admin",
    component: () =>
      import(
        /* webpackChunkName: "super-admin" */ "../views/SuperAdminView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      activeTab: "superAdmin",
    },
  },
  {
    path: "/design-system",
    name: "design-system",
    component: () =>
      import(
        /* webpackChunkName: "design-system" */ "../views/DesignSystemView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      activeTab: "superAdmin",
    },
  },
  {
    path: "/access-denied",
    name: "access-denied",
    component: () => import("../views/AccessDeniedView.vue"),
  },
  {
    path: "/not-found",
    name: "not-found",
    component: () => import("../views/NotFoundView.vue"),
  },
];

routes.push(...developmentOnlyRoutes);

routes.push({
  path: "/:pathMatch(.*)*",
  name: "unmatched-route",
  component: () => import("../views/NotFoundView.vue"),
});

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to) => authenticationRouteGuard(to));
router.beforeEach((to) => superAdminRouteGuard(to));

router.afterEach((to) => {
  if (to.name === "about") {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }
});

export default router;
